import {
	BranchSalesChannelCategoryGroupsDocument,
	type BranchSalesChannelCategoryGroupsQuery,
	type BranchSalesChannelCategoryGroupsQueryVariables,
} from "@/generated/graphql";
import { useCallback } from "react";
import { useClient } from "urql";

const PAGE_SIZE = 200;

// Branch picker limited to branches bound to the campaign's menu group.
export const useMenuGroupBranchAutocomplete = (
	salesChannelCategoryGroupID: string,
	salesChannelID: string,
) => {
	const client = useClient();

	const load = useCallback(
		async (_page: number, pageSize: number, search: string) => {
			if (!salesChannelCategoryGroupID) {
				return { items: [], total: 0, hasMore: false };
			}

			const { data, error } = await client
				.query<
					BranchSalesChannelCategoryGroupsQuery,
					BranchSalesChannelCategoryGroupsQueryVariables
				>(
					BranchSalesChannelCategoryGroupsDocument,
					{
						qop: {
							pagination: {
								page: 1,
								pageSize: Math.max(pageSize, PAGE_SIZE),
								withCount: true,
							},
							filters: { salesChannelCategoryGroupID, salesChannelID },
						},
					},
					{ requestPolicy: "network-only" },
				)
				.toPromise();

			if (error) throw error;

			const keyword = search.trim().toLowerCase();
			// One group binds many categories per branch, so collapse to unique branches.
			const uniqueBranches = new Map<
				string,
				{ branchID: string; branchName: string; branchCode: string }
			>();
			for (const binding of data?.branchSalesChannelCategoryGroups.items ??
				[]) {
				if (uniqueBranches.has(binding.branchID)) continue;
				uniqueBranches.set(binding.branchID, {
					branchID: binding.branchID,
					branchName: binding.branchName,
					branchCode: binding.branchCode,
				});
			}

			const bindings = [...uniqueBranches.values()].filter((branch) => {
				if (!keyword) return true;
				return `${branch.branchName} ${branch.branchCode}`
					.toLowerCase()
					.includes(keyword);
			});

			return {
				items: bindings.map((branch) => ({
					value: branch.branchID,
					label: branch.branchCode
						? `${branch.branchName} (${branch.branchCode})`
						: branch.branchName,
					raw: branch,
				})),
				total: bindings.length,
				hasMore: false,
			};
		},
		[client, salesChannelCategoryGroupID, salesChannelID],
	);

	return { load };
};
